import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { applyEach, form, FormField as SignalFormField, required } from '@angular/forms/signals';
import {
  AppSvgIconComponent,
  SignalButton,
  SignalCheckbox,
  SignalFormDirective,
  SignalSwitch,
  SignalTextField,
  OverlayService,
} from '@vallift/ngl';
import type {
  FormField,
  MultiChoiceGridConfig,
  FormFieldDataLibraryCollectionRef,
} from '../../../../models/built-form';
import { readMultiChoiceGridConfig } from '../../../../models/built-form';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import {
  LinkCollectionButtonComponent,
  type LinkedCollectionResult,
} from '../../../link-collection-button/link-collection-button';
import {
  CollectionColumnPickerOverlayComponent,
  type CollectionColumnPickerResult,
} from '../../../collection-column-picker-overlay/collection-column-picker-overlay';
import {
  resolveCollectionDropdownItems,
  type CollectionSelectOption,
} from '../../../../utils/resolve-collection-dropdown-items';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { constants } from '@core/constants/constants';
import {
  createGridChoice,
  defaultGridChoices,
  type GridChoiceOption,
} from '../../../../utils/grid-choice-option';

type MultiChoiceGridResponseType = 'checkbox' | 'dropdown' | 'button';

interface MultiChoiceGridConfigModel {
  label: string;
  helpText: string;
  responseType: MultiChoiceGridResponseType;
  options: GridChoiceOption[];
  items: string[];
  allowAdditionalRows: boolean;
  addRowButtonLabel: string;
  required: boolean;
}

interface ColumnOption {
  key: string;
  label: string;
}

const DEFAULT_OPTION_LABELS = ['Choice 1', 'Choice 2'] as const;
const DEFAULT_ITEMS = ['Item 1', 'Item 2', 'Item 3'];

@Component({
  selector: 'app-multi-choice-grid-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalCheckbox,
    SignalSwitch,
    SignalFormDirective,
    SignalButton,
    AppSvgIconComponent,
    LinkCollectionButtonComponent,
    CdkDragPlaceholder,
    CdkDrag,
    CdkDropList,
    CdkDragHandle,
  ],
  templateUrl: './multi-choice-grid-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiChoiceGridConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly linkedCollection = signal<FormFieldDataLibraryCollectionRef | null>(null);

  readonly isCollectionLinked = computed(() => this.linkedCollection() !== null);

  readonly collectionColumns = signal<ColumnOption[]>([]);
  readonly collectionItems = signal<CollectionSelectOption[]>([]);

  readonly showAsLabel = computed(() => {
    const linked = this.linkedCollection();

    if (!linked?.displayFieldKey) {
      return '';
    }

    return (
      linked.displayFieldLabel ||
      this.collectionColumns().find((column) => column.key === linked.displayFieldKey)?.label ||
      linked.displayFieldKey
    );
  });

  readonly responseTypeChoices: {
    value: MultiChoiceGridResponseType;
    label: string;
  }[] = [
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'button', label: 'Button' },
  ];

  readonly model = signal<MultiChoiceGridConfigModel>({
    label: 'Multi Choice Grid',
    helpText: '',
    responseType: 'checkbox',
    options: defaultGridChoices(DEFAULT_OPTION_LABELS),
    items: [...DEFAULT_ITEMS],
    allowAdditionalRows: false,
    addRowButtonLabel: '+ Add another item',
    required: false,
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
    applyEach(schema.options, (option) => required(option.label));
    applyEach(schema.items, (item) => required(item));
  });

  private lastFieldId: string | null = null;
  private lastLoadedCollectionId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const field = this.field();

      if (!field || field.id === this.lastFieldId) {
        return;
      }

      this.lastFieldId = field.id;
      this.suppressEmit = true;

      const config = readMultiChoiceGridConfig(field);
      const collection = config.itemsDataLibraryCollection;

      this.linkedCollection.set(
        collection?.dataLibraryId
          ? {
              dataLibraryId: collection.dataLibraryId,
              displayFieldKey: collection.displayFieldKey,
              displayFieldLabel: collection.displayFieldLabel,
              label: collection.label,
              key: collection.key,
            }
          : null,
      );

      this.model.set({
        label: field.label ?? 'Multi Choice Grid',
        helpText: field.helpText ?? 'Select all options that apply.',
        responseType: this.normalizeResponseType(config.responseType),
        options: config.options?.length
          ? config.options.map((option) => ({ ...option }))
          : defaultGridChoices(DEFAULT_OPTION_LABELS),
        items: config.items?.length ? [...config.items] : [...DEFAULT_ITEMS],
        allowAdditionalRows: config.allowAdditionalRows ?? false,
        addRowButtonLabel: config.addRowButtonLabel ?? '+ Add another item',
        required: field.validation?.required ?? field.required ?? false,
      });
    });

    effect(() => {
      const collection = this.linkedCollection();

      if (!collection?.dataLibraryId) {
        this.collectionColumns.set([]);
        this.collectionItems.set([]);
        this.lastLoadedCollectionId = null;
        return;
      }

      if (this.lastLoadedCollectionId === collection.dataLibraryId) {
        return;
      }

      this.lastLoadedCollectionId = collection.dataLibraryId;
      this.loadCollectionMetadata(collection.dataLibraryId);
    });

    // Load items for the selected collection column.
    effect(() => {
      const collection = this.linkedCollection();

      if (!collection?.dataLibraryId || !collection.displayFieldKey) {
        this.collectionItems.set([]);
        return;
      }

      this.loadCollectionItems(collection.dataLibraryId, collection.displayFieldKey);
    });

    effect(() => {
      const draft = this.model();
      const linkedCollection = this.linkedCollection();

      const field = untracked(() => this.field());

      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }

      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      const options = draft.options
        .map((option) => ({
          id: option.id,
          label: option.label.trim(),
        }))
        .filter((option) => option.label.length > 0);

      const items = draft.items
        .map((item) => item.trim())
        .filter(Boolean);

      const nextOptions =
        options.length > 0 ? options : defaultGridChoices(DEFAULT_OPTION_LABELS);

      const existingConfig = readMultiChoiceGridConfig(field);

      const {
        itemsDataLibraryCollection: previousCollection,
        ...configWithoutCollection
      } = existingConfig;

      const config: MultiChoiceGridConfig = {
        ...configWithoutCollection,
        responseType:
          nextOptions.length > 3 ? 'dropdown' : draft.responseType,
        options: nextOptions,
        items,
        allowAdditionalRows: draft.allowAdditionalRows,
        addRowButtonLabel: draft.addRowButtonLabel,
        ...(linkedCollection
          ? {
            itemsDataLibraryCollection: {
              ...previousCollection,
              ...linkedCollection,
            },
          }
          : {}),
      };

      this.fieldChange.emit({
        ...field,
        label: draft.label.trim() || 'Multi Choice Grid',
        helpText: draft.helpText.trim(),
        required: draft.required,
        validation: {
          ...field.validation,
          required: draft.required,
        },
        config,
      });
    });
  }

  readonly previewOptions = computed(() =>
    this.model()
      .options.map((option) => option.label.trim())
      .filter(Boolean),
  );
  readonly effectiveResponseType = computed<MultiChoiceGridResponseType>(() => {
    if (this.previewOptions().length > constants.multiChoiceGridMaxButtonOptions) {
      return 'dropdown';
    }

    return this.model().responseType;
  });
  setResponseType(value: MultiChoiceGridResponseType): void {
    this.model.update((current) => ({
      ...current,
      responseType: value,
    }));
  }

  addOption(): void {
    this.model.update((model) => ({
      ...model,
      options: [...model.options, createGridChoice('')],
    }));
  }

  removeOption(index: number): void {
    this.model.update((model) =>
      model.options.length <= 2
        ? model
        : {
            ...model,
            options: model.options.filter((_, i) => i !== index),
          },
    );
  }

  addItem(): void {
    this.model.update((model) => ({
      ...model,
      items: [...model.items, ''],
    }));
  }

  removeItem(index: number): void {
    this.model.update((model) =>
      model.items.length <= 1
        ? model
        : {
            ...model,
            items: model.items.filter((_, i) => i !== index),
          },
    );
  }

  onOptionDrop(event: CdkDragDrop<GridChoiceOption[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    this.model.update((model) => {
      const options = [...model.options];
      moveItemInArray(options, event.previousIndex, event.currentIndex);

      return {
        ...model,
        options,
      };
    });
  }

  onItemDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    this.model.update((model) => {
      const items = [...model.items];
      moveItemInArray(items, event.previousIndex, event.currentIndex);

      return {
        ...model,
        items,
      };
    });
  }

  onCollectionSelected(result: LinkedCollectionResult): void {
    this.suppressEmit = false;
    this.lastLoadedCollectionId = null;

    this.linkedCollection.set({
      dataLibraryId: result.id,
      displayFieldKey: result.displayFieldKey,
      displayFieldLabel: result.displayFieldLabel,
      label: result.name,
      key: result.key,
    });
  }

  private loadCollectionMetadata(collectionId: string): void {
    this.workspaceApi.getDataLibraryCollection(collectionId).subscribe({
      next: (response) => {
        this.collectionColumns.set(
          (response.data.fields ?? []).map((field) => ({
            key: field.key,
            label: field.name || field.key,
          })),
        );
      },
      error: () => this.collectionColumns.set([]),
    });
  }

  private loadCollectionItems(
    collectionId: string,
    displayFieldKey: string,
  ): void {
    resolveCollectionDropdownItems(
      this.workspaceApi,
      collectionId,
      displayFieldKey,
    ).subscribe({
      next: (items) => {
        const currentCollection = this.linkedCollection();

        if (
          !currentCollection ||
          currentCollection.dataLibraryId !== collectionId ||
          currentCollection.displayFieldKey !== displayFieldKey
        ) {
          return;
        }

        this.collectionItems.set(items);

        const itemDisplays = items
          .map((item) => item.display.trim())
          .filter(Boolean);

        this.model.update((model) => ({
          ...model,
          items: itemDisplays.length
            ? itemDisplays
            : [...DEFAULT_ITEMS],
        }));
      },
      error: () => {
        this.collectionItems.set([]);
      },
    });
  }

  detachLinkedCollection(): void {
    const itemDisplays = this.collectionItems()
      .map((item) => item.display.trim())
      .filter(Boolean);

    this.lastLoadedCollectionId = null;

    this.linkedCollection.set(null);
    this.collectionColumns.set([]);
    this.collectionItems.set([]);

    this.model.update((model) => ({
      ...model,
      items: itemDisplays.length
        ? itemDisplays
        : [...DEFAULT_ITEMS],
    }));
  }

  removeLinkedCollection(): void {
    this.lastLoadedCollectionId = null;

    this.linkedCollection.set(null);
    this.collectionColumns.set([]);
    this.collectionItems.set([]);

    this.model.update((model) => ({
      ...model,
      items: [...DEFAULT_ITEMS],
    }));
  }

  changeShowAsColumn(): void {
    const linked = this.linkedCollection();

    if (!linked) {
      return;
    }

    const dialogRef = this.overlayService.openModal(CollectionColumnPickerOverlayComponent, {
      fitContent: true,
      panelClass: ['w-[22rem]', 'max-w-[calc(100%-2rem)]'],
      data: {
        collectionId: linked.dataLibraryId,
        collectionName: linked.label,
        selectedDisplayFieldKey: linked.displayFieldKey,
      },
    });

    dialogRef.closed.subscribe((result: CollectionColumnPickerResult | null | undefined) => {
      if (!result?.displayFieldKey) {
        return;
      }

      this.linkedCollection.update((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          displayFieldKey: result.displayFieldKey,
          displayFieldLabel: result.displayFieldLabel,
        };
      });
    });
  }

  private normalizeResponseType(
    value: MultiChoiceGridResponseType | null | undefined,
  ): MultiChoiceGridResponseType {
    if (value === 'checkbox' || value === 'dropdown' || value === 'button') {
      return value;
    }

    return 'checkbox';
  }
}
